import React, { useRef } from 'react';
import {
  Autocomplete,
  Box,
  Stack,
  TextField,
} from '@mui/material';
import { Person } from '../types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useScrollIntoView } from '../hooks/useScrollIntoView';

interface Props {
  persons: Person[];
  personId: string;
  onSelectPerson: (id: string) => void;

  isAddingPerson: boolean;
  onAddPersonToggle: (adding: boolean) => void;

  newPersonData: {
    firstName: string;
    lastName: string;
    preferences?: string;
  };
  onChangeNewPerson: (data: { firstName: string; lastName: string; preferences?: string }) => void;
}

export default function StepPersonForm({
  persons,
  personId,
  onSelectPerson,
  isAddingPerson,
  onAddPersonToggle,
  newPersonData,
  onChangeNewPerson,
}: Props) {
  const scrollIntoView = useScrollIntoView();
  const autocompleteRef = useRef<HTMLDivElement>(null);

  const allOptions = [
    ...persons,
    { id: 'add-person', firstName: '', lastName: '+ Add new person' }
  ];

  const handleChange = (_e: any, value: Person | null) => {
    if (!value) return;
    if (value.id === 'add-person') {
      onAddPersonToggle(true);
      onSelectPerson('add-person');
    } else {
      onAddPersonToggle(false);
      onSelectPerson(value.id);
    }
  };

  const handleAutocompleteOpen = () => {
    // Find the input element within the Autocomplete and scroll it into view
    setTimeout(() => {
      const input = autocompleteRef.current?.querySelector('input') || document.activeElement as HTMLInputElement;
      if (input && input instanceof HTMLInputElement) {
        scrollIntoView(input);
      }
    }, 100);
  };

  const handleTextFieldFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    scrollIntoView(e.target);
  };

  return (
    <Box mt={2}>
      <Autocomplete
        ref={autocompleteRef}
        options={allOptions}
        value={persons.find(p => p.id === personId) || null}
        getOptionLabel={(option) =>
          option.id === 'add-person'
            ? option.lastName
            : `${option.firstName} ${option.lastName}`
        }
        renderOption={(props, option) => {
          const label =
            option.id === 'add-person'
              ? "+ Add new person"
              : `${option.firstName} ${option.lastName}`;
          return (
            <li {...props}>
              <Box
                component="span"
                sx={{ fontStyle: option.id === 'add-person' ? 'italic' : 'normal' }}
              >
                {label}
              </Box>
            </li>
          );
        }}
        filterOptions={(options, { inputValue }) => {
          const filtered = options.filter((p) => {
            if (p.id === 'add-person') return true;
            const name = `${p.firstName} ${p.lastName}`.toLowerCase();
            return name.includes(inputValue.toLowerCase());
          });
          const addNew = options.find(o => o.id === 'add-person');
          return [ addNew!, ...filtered.filter(o => o.id !== 'add-person')];
        }}
        renderInput={(params) => (
          <TextField {...params} label="Person" placeholder="Select or search person" />
        )}
        onChange={handleChange}
        onOpen={handleAutocompleteOpen}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />

      {isAddingPerson && (
        <Stack spacing={2} mt={2}>
          <TextField
            fullWidth
            label="First Name"
            value={newPersonData.firstName}
            onChange={(e) =>
              onChangeNewPerson({ ...newPersonData, firstName: e.target.value })
            }
            onFocus={handleTextFieldFocus}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={newPersonData.lastName}
            onChange={(e) =>
              onChangeNewPerson({ ...newPersonData, lastName: e.target.value })
            }
            onFocus={handleTextFieldFocus}
          />

          {/* Preferences rich text field */}
          <Box>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: 4, display: 'block' }}>
              Preferences
            </label>
            <ReactQuill
              theme="snow"
              value={newPersonData.preferences || ''}
              onChange={(value) =>
                onChangeNewPerson({ ...newPersonData, preferences: value })
              }
              modules={{
                toolbar: [
                  ['bold', 'italic', 'underline'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['clean']
                ],
              }}
              style={{
                backgroundColor: '#fff',
                borderRadius: '4px',
              }}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
}
